import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';

import EditorContentUnifiedQuery from './__generated__/EditorContentUnifiedQuery.graphql.ts';
import type {
	EditorContentUnifiedQuery$data,
	EditorContentUnifiedQuery$variables,
} from './__generated__/EditorContentUnifiedQuery.graphql.ts';
import { createEditorContentUnifiedQueryVariables } from './createEditorContentUnifiedQueryVariables.ts';
import { isEditorContentFresh, markEditorContentFetched } from './editorContentFreshness.ts';

/**
 * Route preload of EditorContentUnifiedQuery; EditorContentContainer reads it with the same
 * variables. SSR always fetches; the client reads the store only within the freshness TTL.
 */
export const preloadEditorContentUnifiedQuery = (
	contentId: string,
): ReturnType<
	typeof query<EditorContentUnifiedQuery$data, EditorContentUnifiedQuery$variables>
> => {
	const draftShareId = getDraftShareIdFromQueryParam();
	// Never fresh on SSR, so the server always fetches.
	const isFresh = isEditorContentFresh(contentId, draftShareId);

	return query<EditorContentUnifiedQuery$data, EditorContentUnifiedQuery$variables>({
		query: EditorContentUnifiedQuery,
		variables: createEditorContentUnifiedQueryVariables({ contentId, draftShareId }),
		fetchPolicy: isFresh ? 'cache-first' : 'network-only',
	}).then((result) => {
		// A store read is not new data, so it must not extend the TTL.
		if (!isFresh && result.data?.content) {
			markEditorContentFetched(contentId, draftShareId);
		}
		return result;
	});
};
